import { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';

import { extractFirstLetters } from '@/lib/utils';
import { BACKEND_ENDPOINT } from '@/constants';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Item,
  ItemContent,
  ItemTitle,
  ItemActions,
  Label,
  Separator,
  Skeleton
} from '@bliks-io/ui';

interface OrganizationProfileCardProps {
  name: string;
  logo: string;
  file: File | null;
  error?: string;
  disabled?: boolean;
  onRemove: () => void;
  onFileChange: (files: FileList | null) => void;
}

export function OrganizationProfileCard({
  name,
  logo,
  file,
  error,
  disabled = false,
  onRemove,
  onFileChange
}: OrganizationProfileCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const avatarSrc = useMemo(() => {
    if (file) {
      return URL.createObjectURL(file);
    }
    if (logo) {
      // If logo is already a full URL (from CDN), use it directly
      if (logo.startsWith('http://') || logo.startsWith('https://')) {
        return logo;
      }
      // Otherwise, treat it as a relative path and prefix with backend endpoint
      return `${BACKEND_ENDPOINT}/${logo}`;
    }
    return undefined;
  }, [file, logo]);

  const handleEdit = () => {
    inputRef.current?.click();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Building2 className="w-5 h-5 text-muted-foreground" />
          {t('OrgSettingPage.organizationProfileTitle')}
        </CardTitle>
        <CardDescription>{t('OrgSettingPage.organizationProfileDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Organization Logo */}
        <div className="space-y-3">
          <Label className="text-base font-medium">{t('OrgSettingPage.OrganizationLogo')}</Label>
          <div className="flex items-center gap-6">
            <Avatar size="lg">
              <AvatarImage src={avatarSrc} alt={name} />
              <AvatarFallback className="bg-yellow-avatar text-2xl font-semibold">
                {extractFirstLetters(name)}
              </AvatarFallback>
            </Avatar>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Button size="small" onClick={handleEdit} disabled={disabled}>
                  {file || logo ? t('common.Edit') : t('OrgSettingPage.Upload')}
                </Button>
                {(file || logo) && (
                  <Button size="small" variant="secondary" onClick={onRemove} disabled={disabled}>
                    {t('common.Remove')}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t('OrgSettingPage.logoUploadHint')}</p>
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
          </div>
        </div>

        <Separator />

        {/* Organization Name */}
        <Item>
          <ItemContent>
            <ItemTitle className="text-base">{t('OrgSettingPage.Organizationname')}</ItemTitle>
          </ItemContent>
          <ItemActions>
            <Input className="w-[300px]" value={name} disabled />
          </ItemActions>
        </Item>
      </CardContent>

      <input
        ref={inputRef}
        onChange={(e) => onFileChange(e.target.files)}
        id="dropzone-file"
        type="file"
        className="hidden"
        accept="image/png, image/jpeg, image/svg+xml, image/gif"
      />
    </Card>
  );
}

export function OrganizationProfileCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-56 mb-2" />
        <Skeleton className="h-4 w-80" />
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Logo section */}
        <div className="space-y-3">
          <Skeleton className="h-5 w-40 mb-3" />
          <div className="flex items-center gap-6">
            <Skeleton className="w-24 h-24 rounded-full" />
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-20" />
              </div>
              <Skeleton className="h-4 w-64" />
            </div>
          </div>
        </div>

        <Separator />

        {/* Organization Name */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-10 w-[300px]" />
        </div>
      </CardContent>
    </Card>
  );
}
